import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import SEO from "../../components/SEO";
import { REGISTRATION_LINKS, MEDIA } from "../../lib/constants";

export default function OutOfStateDispensary() {
  return (
    <>
      <SEO
        title="Out-of-State Dispensary Access in Washington, DC | DWC Guide"
        description="Visiting Washington, DC from out of state? You can legally buy medical cannabis at any licensed DC dispensary using your home-state medical ID — or by registering as a temporary patient online in minutes. Here's how."
        path="/out-of-state-dispensary-dc"
        canonical="/blog/out-of-state-dispensary-dc"
        ogType="article"
        breadcrumbs={[
          { name: "Blog", path: "/blog" },
          { name: "Out-of-State Dispensary Access", path: "/blog/out-of-state-dispensary-dc" },
        ]}
        article={{
          publishedTime: "2026-05-12",
          modifiedTime: "2026-09-15",
          section: "Visitor Guide",
        }}
      />

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24 sm:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">For Out-of-State Visitors</div>
            <h1 className="font-serif text-5xl sm:text-7xl leading-[0.95] tracking-tight">
              Buying cannabis in DC <em className="text-brand">from out of state</em>.
            </h1>
            <p className="mt-6 text-ink-muted text-lg max-w-xl leading-relaxed">
              Yes — you can legally buy medical cannabis in Washington, DC even if you don't live here.
              Bring an out-of-state medical card or self-register online. Either way, it takes about 5 minutes.
            </p>
            <div className="mt-8 flex gap-3">
              <a href={REGISTRATION_LINKS.medical} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em]" data-testid="oos-register-medical">
                Register for Medical Cannabis <ArrowUpRight size={14} />
              </a>
              <Link to="/locations" className="inline-flex items-center gap-2 bg-ink text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em]" data-testid="oos-locations">
                <MapPin size={14} /> See Locations
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img src={MEDIA.dc1} alt="Washington DC street" className="w-full h-96 object-cover border border-black/10" />
          </div>
        </div>
      </section>

      <article className="py-20 bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-6 prose prose-lg font-sans text-ink-muted">
          <h2 className="font-serif text-3xl text-ink mt-0 mb-5">The two paths for out-of-state visitors</h2>

          <h3 className="font-serif text-2xl text-ink mt-10 mb-4">Path 1 — Use your home-state medical card</h3>
          <p>
            DC accepts a valid medical cannabis ID from any U.S. state with a medical program. Just bring it
            with a matching photo ID, and you can shop at any licensed DC dispensary, including all three
            District Wellness Center locations. Nothing else to fill out.
          </p>

          <h3 className="font-serif text-2xl text-ink mt-10 mb-4">Path 2 — Register as a temporary DC patient online</h3>
          <p>
            Don't have a home-state card? You can self-register as a temporary DC patient through the OCA portal.
            It's quick — usually under 5 minutes — and works for short-term stays as well as longer visits.
            Pick the registration period that matches your trip (3-day, 30-day, etc.).
          </p>
          <p>
            <a href={REGISTRATION_LINKS.medical} target="_blank" rel="noreferrer" className="text-brand link-underline" data-testid="oos-cta-link">
              Start your medical cannabis registration →
            </a>
          </p>

          <h2 className="font-serif text-3xl text-ink mt-14 mb-5">Where you can buy in DC</h2>
          <p>
            Only at <strong>licensed retailers</strong>. Buying from anywhere else (gifting shops, pop-ups, street vendors)
            is illegal under DC's regulated cannabis program and unsafe — products are unregulated, untested, and unverified.
          </p>
          <p>
            Our DC location is transit-accessible and a short walk from major Metro stops:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>DWC Uptown</strong> — 4915 Georgia Ave NW, Washington, DC 20011 (near Petworth Metro)</li>
          </ul>

          <h2 className="font-serif text-3xl text-ink mt-14 mb-5">FAQ for visitors</h2>
          <p><strong>Can I bring DC cannabis back home?</strong> No. Federally, transporting cannabis across state lines is illegal — even between states that have legalized it.</p>
          <p><strong>Can I consume in my hotel?</strong> Most hotels prohibit smoking and vaping in rooms. Edibles and tinctures are typically more discreet, but always check your hotel's policy.</p>
          <p><strong>Can I use cannabis in public?</strong> No. Public consumption is not permitted in DC. Use it only on private property where the owner allows it.</p>
          <p><strong>What forms of payment can I use?</strong> Cash always works. We also accept compliant digital payment methods. ATMs are on-site at every DWC location.</p>

          <h2 className="font-serif text-3xl text-ink mt-14 mb-5">Tips from our patient specialists</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>If it's your first time, start low: 2.5–5 mg THC for edibles, half a vape pull, or a small joint.</li>
            <li>Tell us what you're trying to <em>achieve</em> — we'll match the product, not the highest THC%.</li>
            <li>Pre-order ahead of busy weekends. Same-day delivery slots fill up.</li>
          </ul>
        </div>
      </article>
    </>
  );
}
